import { ThemeEvents } from '@theme/events';
import { initOptionSelects } from '@theme/product-option-selects';

/**
 * PDP "Shop the collection" row.
 *
 * - Syncs the product image when the user picks a variant via the inline
 *   option <select> dropdowns.
 * - Keeps the hidden variant-ID input and the ATC button in sync with the
 *   currently selected variant.
 * - Updates the colour-swatch dot shown inside the select wrapper whenever
 *   the selected colour changes.
 * - Also listens for external VariantUpdateEvent (e.g. from swatches-variant-
 *   picker elsewhere in the section) to update the media link + thumbnail.
 */
class ShopCollectionRow extends HTMLElement {
  /** @type {Element | null} */
  #section = null;

  /** @type {{ destroy: () => void } | null} */
  #selectsHandle = null;

  connectedCallback() {
    this.#section = this.closest('.shopify-section');
    this.#section?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    this.#initOptionSelects();
  }

  disconnectedCallback() {
    this.#section?.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    this.#section = null;
    this.#selectsHandle?.destroy();
    this.#selectsHandle = null;
  }

  // ── Option dropdowns ────────────────────────────────────────────────────────

  #initOptionSelects() {
    this.#selectsHandle = initOptionSelects(this, {
      onVariantChange: (variant) => {
        if (!variant) return;
        this.#updateVariantInput(variant);
        this.#updateATCButton(variant);
        this.#updateImage(variant);
      },
    });
  }

  /**
   * Updates the hidden variant-ID input and dispatches a change event.
   * @param {Record<string, unknown>} variant
   */
  #updateVariantInput(variant) {
    const variantInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[ref="variantId"]')
    );
    if (variantInput) {
      variantInput.value = String(variant.id);
      variantInput.disabled = !variant.available;
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /**
   * Enables/disables the ATC button based on variant availability.
   * @param {Record<string, unknown>} variant
   */
  #updateATCButton(variant) {
    const addBtn = /** @type {HTMLButtonElement | null} */ (
      this.querySelector('[data-testid="shop-collection-add-to-cart"]')
    );
    if (addBtn) {
      addBtn.disabled = !variant.available;
    }
  }

  /**
   * Swaps the product thumbnail to the variant's featured image (or default).
   * @param {Record<string, unknown>} variant
   */
  #updateImage(variant) {
    const featuredImage = /** @type {Record<string, unknown> | null} */ (
      variant.featured_image ?? null
    );
    const newSrc =
      typeof featuredImage?.src === 'string' && featuredImage.src.length > 0
        ? featuredImage.src
        : null;

    const img = /** @type {HTMLImageElement | null} */ (
      this.querySelector(
        '.shop-the-collection-row__media img.shop-the-collection-row__img:not(.shop-the-collection-row__img--placeholder)'
      )
    );

    if (img) {
      const fallbackSrc = this.dataset.defaultImageSrc?.trim() ?? '';
      const src = newSrc ?? fallbackSrc;
      if (src) {
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
        img.src = src;
      }
    }
  }

  // ── External VariantUpdateEvent (e.g. from swatches elsewhere) ─────────────

  /**
   * @param {import('@theme/events').VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (String(event.detail.data.productId ?? '') !== String(this.dataset.productId ?? '')) return;

    const resource = event.detail.resource;
    if (!resource || typeof resource !== 'object') return;

    const img = this.querySelector(
      '.shop-the-collection-row__media img.shop-the-collection-row__img:not(.shop-the-collection-row__img--placeholder)'
    );
    const defaultSrc = this.dataset.defaultImageSrc?.trim() || '';

    const previewSrc =
      resource.featured_media?.preview_image?.src ||
      (typeof resource.featured_image === 'string'
        ? resource.featured_image
        : resource.featured_image?.src);

    if (previewSrc && typeof previewSrc === 'string' && previewSrc.length > 0) {
      if (img instanceof HTMLImageElement) {
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
        img.src = previewSrc;
      }
    } else if (defaultSrc && img instanceof HTMLImageElement) {
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      img.src = defaultSrc;
    }

    const link = this.querySelector('.shop-the-collection-row__media-link');
    if (link instanceof HTMLAnchorElement && typeof resource.url === 'string' && resource.url.length > 0) {
      try {
        link.href = resource.url;
      } catch {
        /* ignore invalid href */
      }
    }
  };
}

if (!customElements.get('shop-collection-row')) {
  customElements.define('shop-collection-row', ShopCollectionRow);
}
